import {
  type OrderSide, type Error, type Symbol, type ExchangeSymbolIdentity,
} from './common';
import {type TradingAccount} from './account';
import {type OrderRecord, OrderTypes} from './order';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- API schemas explicitly distinguish JSON null from omitted account fields.
type NullValue = null;
/**
 *
 * @todo  front-end vs backend. We don't need the same fields in both. Some or just for front and some are just for server.
 */

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ClientCorrelationID = {
  correlationId: string;

};
export type AlgoParameterItem = {
  algoParameterId: string;
  stringValue?: string;
  intValue?: number;
  doubleValue?: number;
  selectedValues?: string; // comma separate
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export type CFICode = {
  iso10962: string;
};

export enum AlgoRunCommand {
  COMMAND_NONE = 'COMMAND_NONE',
  COMMAND_START = 'COMMAND_START',
  COMMAND_STOP = 'COMMAND_STOP',
  COMMAND_PAUSE = 'COMMAND_PAUSE',
  COMMAND_RESUME = 'COMMAND_RESUME',
}
export type RunAlgoRequestDetails = {
  algoRunId: string;
  command: AlgoRunCommand;
};
export type RunAlgoRequest = {
  runAlgo: RunAlgoRequestDetails;
};

export type RunAlgoResponse = {
  algoRunId: string;
  errorText?: string;
  status: AlgoRunStatus;

};

export type AlgoRunReport = {
  algoTypeId: string;
  algoRunId: string;
  errorText?: string;
  status: AlgoStatus;

  // Account information
  accountId: string;

  traderId: string; // Trader (who implemented the algo)

  // Params
  algoStartTime: Date; // algo Start Time
  algoLaunchTime: Date; // algo Launch Time
  algoEndTime: Date; // algo End Time

  // algo Stats
  // TODO - Move pre tca to it's a different package
  preTcaManual: number;
  preTcaAlgo: number;
  runningCost: number;
  workingOrders: number;
  tradedQty: number;
  grossDelta: number;
  percentComplete: number; // Overall Progress % for overall algo

  // Line Items (ie Trades/Targets)
  items: AlgoRunReportLineItem[];
};

export type AlgoRunReportLineItem = {
  statusMessage: string;

  // Symbol information
  productDescription: string; // ie Mar 21 Corn Future

  symbol: Symbol;
  expiry: Date;

  side: OrderSide;
  orderQuantity: number; // target we want to trade

  // stats - summary
  tradedQuantity: number;
  percentComplete: number;
  cost: number;
  midPrice: number;
  // stats - details
  averageExecutionPrice: number;
  benchmarkPrice: number;
  movedMid: number;
  movedBidAsk: number;
  slippage: number;

  statsCurrency: string; // USD for MVP1
};

/** ********************************************* */

export enum AlgoParameterDataType {
  DATATYPE_DATE = 0,
  DATATYPE_DATETIME = 1,
  DATATYPE_NUMERIC_INT = 2,
  DATATYPE_NUMERIC_FLOAT = 3,
  DATATYPE_SELECT_ONE = 4,
  DATATYPE_SELECT_MUILTIPLE = 5,
  DATATYPE_STRING = 6,
  DATATYPE_SECURITY = 7,
  DATATYPE_PRICE = 8,
  DATATYPE_QUANTITY = 9,
  DATATYPE_ORDER_TYPE = 10,
}

export type AlgoParameterItemResponse = {
  algoParameterId: string;
  input_values: string | number | string[];
};

export type AlgoRunResponse = {
  algoRunId: string;
  success: boolean;
  message: string;
};
export type AlgoUpdate = {
  algoInfo: AlgoInfo;
  accountInfo: AccountInfo;
  runningCost: number;
  preTradeTCA: PreTradeTCA;
  algoProgress: AlgoProgress;
  instruments: AlgoInstrument[];

};

export type AlgoInfo = {
  algo_run_id: string;
  algoType: string;
  algoName: string;
  launchedDate: Date;
  createdDate: Date;
  completedDate: Date;
  startTime: Date;
  endTime: Date;
  algoStatus: AlgoRunStatus;

  aggressiveness: string;
  leggingRisk: number;
  placedBy?: string;
  abortedBy?: string;
  abortedDate?: string;
  pausedDate?: string;
};

export type AlgoProgress = {
  workingOrders: number;
  tradedQuantity: number;
  grossDelta: number;
  percentComplete: number;
};
// eslint-disable-next-line @typescript-eslint/naming-convention
export type PreTradeTCA = {
  manual: number;
  algo: number;
};
export type AlgoInstrument = {
  symbol: Symbol;
  runCost: number;
  quantityRequested: number;
  benchmarkPrice: number;
  movedMid: price;
  movedBid: price;
  movedAsk: price;
  slippage: number;
  orderSide: OrderSide;
  errorCodes: Error[];
  percentComplete: number;
  contractsTraded: number;
};
// eslint-disable-next-line @typescript-eslint/naming-convention
export type price = {
  price: number;
  currency: string;
};
export type StartAlgoRequest = {
  algoRunId: string;
};

export type RemoveAlgoRequest = {
  algoRunId: string;
};

export type AlgoDetails = {
  algoType: string;
  status: AlgoStatus;
  id: string;
  date: string;
  symbols: Symbol[];
  totalQuantity: number;
  tradedQuantity: number;
  remainingQuantity: number;
  estimatedTCA: number;
  runCost: number;
  totalSaving: number;
  trader: string;
  exchange: string;
  currency: string;
  broker: string;
};

export type AlgoSavingsByDate = {
  date: string;
  totalSavings: number;
};

export type AlgoDateRange = {
  start: string;
  end: string;
};

// Basket screen
export type AlgoClonePayload = {
  symbols: Symbol[];
  algoName?: string;
  orderParameters: OrderAlgoParameters;
  orderLegs: OrderRecord[];
  tradingAccount: TradingAccount;
  completeRemaining?: boolean;
};

export type OrderAlgoParameters = Record<string, any>;

type Aggressivness = 'Standard' | 'Aggressive';
type ExecutionApproach = 'PayToComplete' | 'BestEfforts';

export type AlgoParams = {
  algoId: AlgoTypeString | `JPM_${string}`;
  duration?: string | number;
  launchImmediately?: boolean;
  startTime?: string | Date | undefined;
} & AlgoExtraParameters;

type AlgoExtraParameters = Record<string, any>;

/**
 *
 * New Algo Types
 *
 *
 */

export type NewAlgoRequest = {
  newAlgo: NewAlgoRequestDetails;
};

export type NewAlgoRequestDetails = {
  requestTimeUtc?: Date;
  clientCorrelationId?: ClientCorrelationID;
  traderId?: string;
  businessUnit?: string;
  accountInfo?: AccountInfo;
  algoRunConstructionResult?: AlgoRunConstructionResult;
  algoRunRequest: AlgoRunRequest;
};

export type NewAlgoModifyRequestDetails = {
  algoRunId: string;
} & NewAlgoRequestDetails;

export type AlgoRunConstructionResult = {
  resultCode: number;
  resultValue: number;
  resultDetails: string;
};
export type AlgoRunRequest = {
  algoType: AlgoTypeString;
  baseCurrencyCodeIso: string;
  customLabel?: string;
  launchImmediately: boolean;
  startTimeRequestedUtc: Date;
  endTimeRequestedUtc?: Date;
  durationInSeconds?: number;
  setBenchmarkPricesAsOfLaunch?: boolean;
  algoTradeRequests: AlgoTradeRequest[];
  additionalParameters?: AlgoParameterItem[];
  removeParameters?: AlgoParameterItem[];
};

export enum AlgoType {
  ALGOTYPE_NOTSET = 0,
  ALGOTYPE_QEP = 1,
  ALGOTYPE_SINGLELEG = 2,
  ALGOTYPE_MULTILEG = 3,
  ALGOTYPE_SINGLELEG_LIQUIDITY_SEEKER = 4,
  ALGOTYPE_MULTILEG_LIQUIDITY_SEEKER = 5,
  // These are not valid values. Only here so that this enum can be used as the id property in dropdowns
  ALGOTYPE_SINGLELEG_ICEBERG = 1000,
  ALGOTYPE_SINGLELEG_TWAP = 2000,
}

export enum AlgoTypeString {
  ALGOTYPE_NOTSET = 'ALGOTYPE_NOTSET',
  ALGOTYPE_QEP = 'ALGOTYPE_QEP',
  ALGOTYPE_SINGLELEG = 'ALGOTYPE_SINGLELEG',
  ALGOTYPE_MULTILEG = 'ALGOTYPE_MULTILEG',
  ALGOTYPE_SINGLELEG_LIQUIDITY_SEEKER = 'ALGOTYPE_SINGLELEG_LIQUIDITY_SEEKER',
  ALGOTYPE_MULTILEG_LIQUIDITY_SEEKER = 'ALGOTYPE_MULTILEG_LIQUIDITY_SEEKER',
  ALGOTYPE_SINGLELEG_ICEBERG = 'ALGOTYPE_SINGLELEG_ICEBERG',
  ALGOTYPE_SINGLELEG_TWAP = 'ALGOTYPE_SINGLELEG_TWAP',
  // Placeholder until JPM algos can go live
  // JPM_AQUA = 'JPM_AQUA',
  // JPM_VWAP = 'JPM_VWAP',
  // JPM_TWAP = 'JPM_TWAP',
  // JPM_TIMESLICER = 'JPM_TIMESLICER',
  // JPM_POV = 'JPM_POV',
  // JPM_PEG = 'JPM_PEG',
  // JPM_ICEBERG = 'JPM_ICEBERG',
  // JPM_CLOAK = 'JPM_CLOAK',
  // JPM_DISCRETION = 'JPM_DISCRETION',
  // JPM_DISCRETION_PLUS = 'JPM_DISCRETION_PLUS',
  // JPM_TARGET = 'JPM_TARGET',
}
export type AlgoTradeRequest = {
  symbolIdentity: ExchangeSymbolIdentity;
  contractInfo?: Record<string, unknown>;
  side: OrderSide | string;
  targetQuantity: number;
  benchmarkPrice?: number;
  benchmarkGreeks?: GreekValues;
  fittedVolBenchmark?: number;
  accountInfo?: AccountInfo;
  additionalParameters?: AlgoParameterItem[];
  maxChildOrderSize?: number;
};

export type ModifyAlgoRequest = {
  requestTimeUtc?: Date;
  algoRunId: string;
  clientCorrelationId?: ClientCorrelationID;
  traderId?: string;
  algoRunModifyRequest: AlgoRunModifyRequest;
};

export type AlgoRunModifyRequest = {
  launchImmediately?: boolean;
  startTimeRequestedUtc?: Date;
  endTimeRequestedUtc?: Date;
  algoTradeModifyRequests?: AlgoTradeModifyRequest[];
  additionalParameters?: AlgoParameterItem[];
  removeParameters?: AlgoParameterItem[];
};

export type AlgoTradeModifyRequest = {
  symbolIdentity: ExchangeSymbolIdentity;
  targetQuantity?: number;
};

export type ModifyAlgoResponse = {
  responseTimeUtc: Date;
  clientCorrelationId: ClientCorrelationID;
  algoRunStatus: AlgoRunStatus;
};

export type AccountInfo = {
  brokerCode?: string;
  clearingAccount?: string;
  internalAccountCode: string;
  externalAccountCode?: string;
  executionTag?: string;
  execCode?: string;
  customText1?: string;
  customText2?: string;
  customText3?: string;
  memo?: string;
  senderLocationId?: string;
  legalEntityIdentifier?: string;
  selfMatchPreventionId?: string;
  neovestAccountCode?: string;
  traderJurisdiction?: string;
  businessUnit?: string;
  deliverToCompId: string;
  platform?: string;
};

export type GreekValues = {
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
};

export type NewAlgoResponse = {
  newAlgo: NewAlgoResponseDetails;
};
export type NewAlgoResponseDetails = {
  responseTimeUtc: Date;
  clientCorrelationId: ClientCorrelationID;
  algoRunStatus: AlgoRunStatus;
  startTimeUtc: Date;
  endTimeUtc: Date;
};
export type AlgoRunStatus = {
  refTimeUtc: Date;
  algoRunId: string;
  status: AlgoStatusString;
  statusDetails: string;
  traderId: string;
};

export enum AlgoStatusString {
  ALGOSTATUS_NOTSET = 'ALGOSTATUS_NOTSET',
  ALGOSTATUS_INITIALIZING = 'ALGOSTATUS_INITIALIZING',
  ALGOSTATUS_INITIALIZED = 'ALGOSTATUS_INITIALIZED',
  ALGOSTATUS_VALIDATING = 'ALGOSTATUS_VALIDATING',
  ALGOSTATUS_INVALID = 'ALGOSTATUS_INVALID',
  ALGOSTATUS_LAUNCH_REQUESTED = 'ALGOSTATUS_LAUNCH_REQUESTED',
  ALGOSTATUS_ABORT_REQUESTED = 'ALGOSTATUS_ABORT_REQUESTED',
  ALGOSTATUS_PAUSE_REQUESTED = 'ALGOSTATUS_PAUSE_REQUESTED',
  ALGOSTATUS_RESUME_REQUESTED = 'ALGOSTATUS_RESUME_REQUESTED',
  ALGOSTATUS_RUNNING = 'ALGOSTATUS_RUNNING',
  ALGOSTATUS_PAUSED = 'ALGOSTATUS_PAUSED',
  ALGOSTATUS_COMPLETE = 'ALGOSTATUS_COMPLETE',
  ALGOSTATUS_INCOMPLETE_REACHED_END_TIME = 'ALGOSTATUS_INCOMPLETE_REACHED_END_TIME',
  ALGOSTATUS_INCOMPLETE_MARKET_CLOSED = 'ALGOSTATUS_INCOMPLETE_MARKET_CLOSED',
  ALGOSTATUS_ABORTED = 'ALGOSTATUS_ABORTED',
  ALGOSTATUS_ERROR = 'ALGOSTATUS_ERROR',
  ALGOSTATUS_ABORT_REQUESTED_BY_SYSTEM = 'ALGOSTATUS_ABORT_REQUESTED_BY_SYSTEM',
  ALGOSTATUS_ABORTED_BY_SYSTEM = 'ALGOSTATUS_ABORTED_BY_SYSTEM',
  ALGOSTATUS_MODIFY_REQUESTED = 'ALGOSTATUS_MODIFY_REQUESTED',
  ALGOSTATUS_OTHER = 99,
}

export enum AlgoStatusStringHumanReadable {
  ALGOSTATUS_NOTSET = 'Not set',
  ALGOSTATUS_INITIALIZING = 'Initializing',
  ALGOSTATUS_INITIALIZED = 'Initialized',
  ALGOSTATUS_VALIDATING = 'Validating',
  ALGOSTATUS_INVALID = 'Invalid',
  ALGOSTATUS_LAUNCH_REQUESTED = 'Launch requested',
  ALGOSTATUS_ABORT_REQUESTED = 'Abort requested',
  ALGOSTATUS_PAUSE_REQUESTED = 'Pause requested',
  ALGOSTATUS_RESUME_REQUESTED = 'Resume requested',
  ALGOSTATUS_RUNNING = 'Running',
  ALGOSTATUS_PAUSED = 'Paused',
  ALGOSTATUS_COMPLETE = 'Complete',
  ALGOSTATUS_INCOMPLETE_REACHED_END_TIME = 'Incomplete; Reached end of time',
  ALGOSTATUS_INCOMPLETE_MARKET_CLOSED = 'Incomplete; Market closed',
  ALGOSTATUS_ABORTED = 'Aborted',
  ALGOSTATUS_ERROR = 'Error',
  ALGOSTATUS_ABORT_REQUESTED_BY_SYSTEM = 'Abort requested by system',
  ALGOSTATUS_ABORTED_BY_SYSTEM = 'Aborted by system',
  ALGOSTATUS_OTHER = 'Unknown status',
}

export enum AlgoStatus {
  ALGOSTATUS_NOTSET = 0,
  ALGOSTATUS_INITIALIZING = 1,
  ALGOSTATUS_INITIALIZED = 2,
  ALGOSTATUS_VALIDATING = 3,
  ALGOSTATUS_INVALID = 4,
  ALGOSTATUS_LAUNCH_REQUESTED = 5,
  ALGOSTATUS_ABORT_REQUESTED = 6,
  ALGOSTATUS_PAUSE_REQUESTED = 7,
  ALGOSTATUS_RESUME_REQUESTED = 8,
  ALGOSTATUS_RUNNING = 9,
  ALGOSTATUS_PAUSED = 10,
  ALGOSTATUS_COMPLETE = 11,
  ALGOSTATUS_INCOMPLETE_REACHED_END_TIME = 12,
  ALGOSTATUS_INCOMPLETE_MARKET_CLOSED = 13,
  ALGOSTATUS_ABORTED = 14,
  ALGOSTATUS_ERROR = 15,
  ALGOSTATUS_ABORT_REQUESTED_BY_SYSTEM = 16,
  ALGOSTATUS_ABORTED_BY_SYSTEM = 17,
  ALGOSTATUS_OTHER = 99,
}

export type AlgoRunReportUpdate = {
  algoRunReportUpdate: AlgoRunReportUpdateDetails;
};
export type AlgoRunReportUpdateDetails = {
  status: AlgoRunStatus;
  accountInfo: AccountInfo;
  canTradeOverall: boolean;
  newAlgoRequest: NewAlgoRequestDetails;
  refTimeUtc: string;
  startTimeUtc: string;
  endTimeUtc: string;
  launchTimeUtc: string;
  lastPauseTimeUtc: string;
  lastResumeTimeUtc: string;
  abortTimeUtc: string;
  completedTimeUtc: string;
  percentTimePassed: number;
  targetQuantityOverall: number;
  tradedQuantityOverall: number;
  remainingQuantityOverall: number;
  numWorkingOrdersOverall: number;
  percentCompleteLongDelta: number;
  percentCompleteShortDelta: number;
  percentCompleteGrossDelta: number;
  preTradeTcaManual: number;
  preTradeTcaAlgo: number;
  executionMetrics: ExecutionMetrics;
  fxBaseToDollars: number;
  algoTradeReports: AlgoTradeReportUpdate[];

};
export type ExecutionMetrics = {
  valueBenchmark?: LocalBaseDollarCurrencyValues;
  valueBenchmarkAdjustedByUnderlyingChange?: LocalBaseDollarCurrencyValues;
  valueMid?: LocalBaseDollarCurrencyValues;
  valueWeightedMid?: LocalBaseDollarCurrencyValues;
  valueFitted?: LocalBaseDollarCurrencyValues;
  valueBidAsk?: LocalBaseDollarCurrencyValues;
  valueSweepToFill?: LocalBaseDollarCurrencyValues;
  tradedSlippage?: LocalBaseDollarCurrencyValues;
  remainingMovedMid?: LocalBaseDollarCurrencyValues;
  remainingMovedWeightedMid?: LocalBaseDollarCurrencyValues;
  remainingMovedFitted?: LocalBaseDollarCurrencyValues;
  remainingMovedBidAsk?: LocalBaseDollarCurrencyValues;
  remainingMovedSweepToFill?: LocalBaseDollarCurrencyValues;
  targetDeltaValuesLong?: LocalBaseDollarCurrencyValues;
  targetDeltaValuesShort?: LocalBaseDollarCurrencyValues;
  tradedDeltaValuesLong?: LocalBaseDollarCurrencyValues;
  tradedDeltaValuesShort?: LocalBaseDollarCurrencyValues;
  runningCost?: LocalBaseDollarCurrencyValues;
};

export type AlgoTradeReportUpdate = {
  algoTradeAutoId: number;
  algoTradeRequest: AlgoTradeRequest;
  status: AlgoTradeStatus;
  statusDetails: string;
  canTrade: boolean;
  benchmarkPrice: number;
  launchPrice: number;
  midPriceAtTradeCompletion: number;
  midPriceAtEndOfAlgo: number;
  tradedQuantity: number;
  remainingQuantity: number;
  percentComplete: number;
  averageFillPrice: number;
  percentAheadOrBehind: number;
  benchmarkGreeks: GreekValues;
  fittedBolBenchmark: number;
  executionMetrics: ExecutionMetrics;
  fxLocalToDollars: number;
  details: string;
};

export type LocalBaseDollarCurrencyValues = {
  localValue: number;
  baseValue: number;
  dollarsValue: number;
};
export enum AlgoTradeStatus {
  ALGOTRADE_NOTSET = 0,
  ALGOTRADE_CAN_TRADE = 1,
  ALGOTRADE_EXECUTED_TARGET_QUANTITY = 2,
  ALGOTRADE_EXECUTED_TARGET_QUANTITY_IN_WINDOW = 3,
  ALGOTRADE_PRICE_CHANGE_CONSTRAINT = 4,
  ALGOTRADE_UNDERLYING_PRICE_CHANGE_CONSTRAINT = 5,
  ALGOTRADE_VOLATILITY_CHANGE_CONSTRAINT = 6,
  ALGOTRADE_MARKET_PRICES_NOT_VALID = 7,
  ALGOTRADE_MARKET_PRICES_STALE = 8,
  ALGOTRADE_MARKET_PRICES_INVERTED = 9,
  ALGOTRADE_MARKET_PRICES_QUOTE_MISSING = 10,
  ALGOTRADE_FITTED_PRICES_NOT_VALID = 11,
  ALGOTRADE_FITTED_PRICES_INVERTED = 12,
  ALGOTRADE_FITTED_PRICES_NOT_OVERLAP_MARKET_PRICES = 13,
  ALGOTRADE_BIDASK_SPREAD_ISSUE = 14,
  ALGOTRADE_BIDASK_SPREAD_TOO_WIDE = 15,
  ALGOTRADE_ORDER_PRICE_ISSUE = 16,
  ALGOTRADE_ORDER_SIZE_ISSUE = 17,
  ALGOTRADE_FIT_QUALITY_ISSUE = 18,
  ALGOTRADE_DELTA_CONSTRAINT = 19,
  ALGOTRADE_TOOK_LIQUIDITY = 20,
  ALGOTRADE_TOO_FAR_AHEAD = 21,
  ALGOTRADE_OTHER_TRADE_OUT_OF_STEP = 22,
  ALGOTRADE_ORDER_TYPE_ISSUE = 23,
  ALGOTRADE_EXECUTED_MORE_THAN_TARGET = 24,
  ALGOTRADE_MARKET_REJECT = 25,
  ALGOTRADE_ALGORUN_PAUSED = 26,
  ALGOTRADE_ALGORUN_ISSUE = 90,
  ALGOTRADE_OTHER = 99,
}

/**
 *
 *
 * AlgoDefinitionsRequest
 */

export type AlgoDefinitionsRequest = {
  algoDefinitions: AlgoDefinitionsRequestDetails;
};

export type AlgoDefinitionsRequestDetails = {
  requestTimeUtc: Date;
  traderId: string;
  clientCorrelationId: ClientCorrelationID;
};

export type AlgoDefinitionsResponse = {
  algoDefinitions: AlgoDefinitionsResponseDetails;
};
export type AlgoDefinitionsResponseDetails = {
  responseTimeUtc: Date;
  definition: AlgoDefinition[];
  clientCorrelationId: ClientCorrelationID;
};

export type AlgoDefinition = {
  algoType: AlgoTypeString | `JPM_${string}`;
  shortDescription: string;
  longDescription: string;
  parameterDefinitions: AlgoParameterDefinition[];
  displayName?: string;
};

export type AlgoParameterDefinition = {
  algoParameterId: string;
  displayName: string;
  dataType?: AlgoParameterDataType;
  numericMaxValue?: number;
  numericMinValue?: number;
  possibleSelectValues?: string[];
  parameterGroup?: string;
  repeatingGroup?: boolean;
  isOptional?: boolean;
  hasDefault?: boolean;
  repeatMax?: number;
  shortDescription?: string;
  longDescription?: string;
  defaultValue?: string;
};

export type PauseAlgoRequest = {
  pauseAlgo: PauseAlgoRequestDetails;
};

export type PauseAlgoRequestDetails = {
  requestTimeUtc: Date;
  algoRunId: string;
  clientCorrelationId: ClientCorrelationID;

};

export type ResumeAlgoRequest = {
  resumeAlgo: ResumeAlgoRequestDetails;
};
export type ResumeAlgoRequestDetails = {
  requestTimeUtc: Date;
  algoRunId: string;
  clientCorrelationId: ClientCorrelationID;

};
export type AbortAlgoRequest = {
  abortAlgo: AbortAlgoRequestDetails;
};
export type AbortAlgoRequestDetails = {
  requestTimeUtc: Date;
  algoRunId: string;
  clientCorrelationId: ClientCorrelationID;

};

export type AlgoTradeAccountInfo = {
  internal_account_code?: string | NullValue;
  external_account_code?: string | NullValue;
  execution_tag?: string | NullValue;
  custom_text1?: string | NullValue;
  custom_text2?: string | NullValue;
  custom_text3?: string | NullValue;
  memo?: string | NullValue;
  neovest_account_code?: string | NullValue;
};

export enum AlgoLeggingRiskTypes {
  ALGOTYPE_MULTILEG_LEGGING_RISK_BASE_CURRENCY = 'ALGOTYPE_MULTILEG_LEGGING_RISK_BASE_CURRENCY',
  ALGOTYPE_MULTILEG_LIQUIDITY_SEEKER_LEGGING_RISK_BASE_CURRENCY = 'ALGOTYPE_MULTILEG_LIQUIDITY_SEEKER_LEGGING_RISK_BASE_CURRENCY',
}

export enum AlgoLimitNetTargetTypes {
  ALGOTYPE_MULTILEG_LIMIT_NET_TARGET_VALUE_BASE_CURRENCY = 'ALGOTYPE_MULTILEG_LIMIT_NET_TARGET_VALUE_BASE_CURRENCY',
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const AlgoCustomLimitTypes = {...AlgoLeggingRiskTypes, ...AlgoLimitNetTargetTypes};

export enum JpmAlgoTagStrings {
  AQUA = 'JPM_AQUA',
  VWAP = 'JPM_VWAP',
  TWAP = 'JPM_TWAP',
  TIMESLICER = 'JPM_TIMESLICER',
  POV = 'JPM_POV',
  PEG = 'JPM_PEG',
  ICEBERG = 'JPM_ICEBERG',
  CLOAK = 'JPM_CLOAK',
  DISCRETION = 'JPM_DISCRETION',
  DISCRETIONPLUS = 'JPM_DISCRETION_PLUS',
  TARGET = 'JPM_TARGET',
}
